import {testIdProps} from '@github-ui/test-id-props'
import {ChevronRightIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {memo} from 'react'

import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {useViewType} from '../../hooks/use-view-type'
import {useViews} from '../../hooks/use-views'
import {type ConfigurationOption, optionsForViewType} from './configuration'

type ListItemType = React.PropsWithChildren<
  Omit<ConfigurationOption, 'TextContent'> & {
    onSelect: (option: ConfigurationOption['title']) => void
    disabled?: boolean
    ref?: React.Ref<HTMLLIElement>
  }
>

const ListItem = memo(({ref, title, testId, children, disabled, icon: Icon, onSelect}: ListItemType) => {
  return (
    <ActionList.Item
      ref={ref}
      disabled={disabled}
      {...testIdProps(`view-options-menu-item-${testId}`)}
      onSelect={e => {
        onSelect(title)
        e.preventDefault()
      }}
    >
      <ActionList.LeadingVisual>
        <Icon />
      </ActionList.LeadingVisual>

      {children}

      <ActionList.TrailingVisual>
        <ChevronRightIcon />
      </ActionList.TrailingVisual>
    </ActionList.Item>
  )
})

ListItem.displayName = 'ListItem'

type ConfigurationItemsProps = {
  selectedOption: ConfigurationOption['title'] | null
  onSelect: (option: ConfigurationOption['title']) => void
  ref?: React.Ref<HTMLLIElement>
}

export const ConfigurationItems = memo(({ref, onSelect, selectedOption}: ConfigurationItemsProps) => {
  const {viewType} = useViewType()
  const {currentView} = useViews()
  const enabledFeatures = useEnabledFeatures()

  const options = optionsForViewType[viewType]
  if (!options || !currentView) return null

  return (
    <ActionList.Group variant="subtle" aria-label="Configuration">
      {options.map(option => {
        if (option.hideIf({currentView, enabledFeatures})) return null

        return (
          <ListItem
            key={option.title}
            {...option}
            ref={option.title === selectedOption ? ref : undefined}
            onSelect={onSelect}
          >
            <option.TextContent />
          </ListItem>
        )
      })}
    </ActionList.Group>
  )
})

ConfigurationItems.displayName = 'ConfigurationItems'

export const ConfigurationItemMenus = memo(function ConfigurationItemMenus({
  currentMenu,
  setShowSubMenu,
  anchorRef,
}: {
  currentMenu: ConfigurationOption['title'] | null
  setShowSubMenu: (next: ConfigurationOption['title'] | null) => void
  anchorRef: React.RefObject<HTMLElement | null>
}) {
  const {viewType} = useViewType()
  const {currentView} = useViews()
  const enabledFeatures = useEnabledFeatures()

  const options = optionsForViewType[viewType]
  if (!options || !currentView) return null

  return (
    <>
      {options.map(({MenuComponent, title, ...option}) => {
        if (option.hideIf({currentView, enabledFeatures})) return null

        return (
          <MenuComponent
            key={title}
            anchorRef={anchorRef}
            open={currentMenu === title}
            setOpen={nextOpen => {
              setShowSubMenu(nextOpen ? title : null)
            }}
          />
        )
      })}
    </>
  )
})

try{ ConfigurationItemMenus.displayName ||= 'ConfigurationItemMenus' } catch {}